import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  DialogActions,
  Button,
  InputAdornment,
  Typography,
  IconButton,
} from '@mui/material';
import { RHFSelect, RHFTextField } from '../hook-form';
import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import FormProvider from '../hook-form/form-provider';
import { postRequest } from 'src/services';
import * as Yup from 'yup';
import { useForm, useFieldArray } from 'react-hook-form';

import { handleErrorMessages } from '../risk-management/risk-register/feching';
import Iconify from '../iconify';
import { Descriptions } from './dropdown-descirption';
import { calculateTotalCosts, reCalculateAllTotals } from './activity-models-calculations';
import ModalTotals from './modals-totals';
import ModalPercentages from './modals-percentages';

const TaxModal = ({ isOpen, handleClose, reload, PercentIcon, hideDetails }) => {
  const [loading, setLoading] = useState(false);
  const NewUserSchema = Yup.object().shape({});
  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues: {
      tax_name: '',
      tax_code: '',
      total_labor_cost: '',
      total_material_cost: '',
      total_equipment_cost: '',
      total_subcontractor_cost: '',
      total_other_cost: '',
      total_overall_cost: '',
      taxes: [
        {
          description: '',
          labor_cost: '',
          material_cost: '',
          equipment_cost: '',
          subcontractor_cost: '',
          other_cost: '',
          overall_cost: '',
        },
      ],
    },
  });

  const { reset, watch, setValue, handleSubmit, control } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: 'taxes' });

  const values = watch();

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const onClose = () => {
    handleClose();
    reset();
  };

  const onSubmit = handleSubmit(async (formData) => {
    setLoading(true);
    try {
      const responseData = await postRequest('', formData);
      if (responseData.metadata && responseData.metadata.success) {
        onClose();
        reload();
        enqueueSnackbar('Taxes created successfully', { variant: 'success' });
      } else {
        handleErrorMessages(responseData.metadata.message);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      enqueueSnackbar('An error occurred while submitting the form.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  });

  const calculationOfTotalCosts = useCallback(calculateTotalCosts(values.taxes, setValue), [
    values,
  ]);

  const reCalculationOfAllTotals = useCallback(reCalculateAllTotals(setValue), [values]);

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxSizing: 'content-box',
          }}
        >
          <span
            className="cardColor"
            style={{ padding: '7px', marginLeft: '-25px', color: 'white', fontWeight: '700' }}
          >
            &nbsp;&nbsp; Taxes &nbsp;&nbsp;&nbsp;&nbsp;
          </span>
          <IconButton onClick={handleClose}>
            <Iconify icon="mdi:close" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box
            mt={2}
            mb={2}
            gap={3}
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(2, 1fr)',
              lg: 'repeat(2, 1fr)',
            }}
          >
            <RHFTextField name="tax_name" label="Taxes Table Name" required />
            <RHFTextField name="tax_code" label="Taxes Code " required />
            <ModalTotals PercentIcon={PercentIcon} hideDetails={hideDetails} />
          </Box>
          <Divider sx={{ my: 2 }} />
          {fields?.map((field, index) => (
            <Box key={field.id} mt={2} mb={2}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="subtitle1">
                  Taxes {index + 1}/{fields.length}
                </Typography>
                <Box>
                  {fields.length > 1 && (
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() => {
                        remove(index);
                        reCalculationOfAllTotals(values.taxes.filter((_, i) => i !== index));
                      }}
                      sx={{ marginRight: 1 }}
                    >
                      Remove
                    </Button>
                  )}
                  <LoadingButton
                    className="dark-button"
                    variant="contained"
                    onClick={() =>
                      append({
                        description: '',
                        labor_cost: '',
                        material_cost: '',
                        equipment_cost: '',
                        subcontractor_cost: '',
                        other_cost: '',
                        overall_cost: '',
                      })
                    }
                  >
                    Add Tax
                  </LoadingButton>
                </Box>
              </Box>
              <Box
                gap={3}
                display="grid"
                gridTemplateColumns={{
                  xs: 'repeat(2, 1fr)',
                  lg: 'repeat(2, 1fr)',
                }}
              >
                <RHFSelect
                  native
                  name={`taxes[${index}].description`}
                  label="Description"
                  InputLabelProps={{ shrink: true }}
                  required
                >
                  <option disabled value="">
                    Select Description
                  </option>
                  {Descriptions?.map((description) => (
                    <optgroup key={description.group} label={description.group}>
                      {description.classify.map((classify) => (
                        <option
                          key={classify}
                          value={classify}
                          disabled={values.taxes.find((item) => item.description === classify)}
                        >
                          {classify}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </RHFSelect>
                <ModalPercentages
                  type="taxes"
                  index={index}
                  PercentIcon={PercentIcon}
                  hideDetails={hideDetails}
                  calculationOfTotalCosts={calculationOfTotalCosts}
                />
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            className="dark-button"
            disabled={loading}
            loading={loading}
            type="submit"
            variant="contained"
          >
            Save
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};
export default TaxModal;
